<script setup lang="ts">
import { useAppKit } from '@reown/appkit/vue';
import { blo } from 'blo';
import { computed, ref } from 'vue';
import { useClipboardCopy } from '@src/shared/lib/composables/useClipboardCopy';
import { useWalletSession } from '@src/shared/lib/composables/useWalletSession';
import { vClickOutside } from '@src/shared/lib/directives/clickOutside';

const WALLET_AVATAR_SIZE = 24;

const { address, addressShort, disconnect, isConnected } = useWalletSession();

const avatarSrc = computed(() => {
	const addr = address.value;
	return addr ? blo(addr, WALLET_AVATAR_SIZE) : undefined;
});

const { open } = useAppKit();

const isOpen = ref(false);
const { copy, isCopied } = useClipboardCopy();

function toggleDropdown() {
	isOpen.value = !isOpen.value;
}

function closeDropdown() {
	isOpen.value = false;
}

function openConnectModal() {
	void open();
}

async function copyAddress() {
	await copy(address.value);
}

function onDisconnect() {
	disconnect();
	closeDropdown();
}
</script>

<template>
	<div v-click-outside="closeDropdown" class="relative" @keydown.esc="closeDropdown">
		<button
			v-if="!isConnected"
			type="button"
			class="flex h-10 sm:h-11 cursor-pointer items-center rounded-full border border-cardBorder bg-white px-6 text-black transition-all hover:shadow-lg-rs hover:shadow-white/20 hover:ring-1 hover:ring-primaryText"
			@click="openConnectModal"
		>
			Connect Wallet
		</button>

		<template v-else>
			<button
				type="button"
				class="flex h-10 sm:h-11 cursor-pointer items-center gap-2 rounded-full border border-cardBorder bg-cardBg transition-all hover:border-cardBorderHover px-4 text-primaryText"
				aria-haspopup="true"
				:aria-expanded="isOpen"
				@click="toggleDropdown"
			>
				<img v-if="avatarSrc" :src="avatarSrc" :alt="address ?? ''" class="h-6 w-6 shrink-0 rounded-full" />
				<span>{{ addressShort }}</span>
				<img
					src="/src/shared/assets/icons/fill-arrow-dropdown.svg"
					class="h-2 w-2 shrink-0 transition-transform duration-200"
					:class="isOpen ? 'rotate-180' : 'rotate-0'"
					alt=""
				/>
			</button>

			<Transition name="dropdown">
				<div
					v-if="isOpen"
					class="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-cardBorder bg-mainBg p-3 shadow-lg shadow-black/40"
				>
					<p class="mb-2 text-xs uppercase tracking-wide text-secondaryText">Wallet</p>

					<button
						type="button"
						class="flex w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-2 text-sm transition hover:bg-white/10"
						@click="copyAddress"
					>
						<span class="min-w-0 truncate font-mono text-primaryText">{{ addressShort }}</span>
						<span class="flex shrink-0 items-center text-secondaryText" aria-hidden="true">
							<template v-if="isCopied">
								<svg
									class="h-4 w-4 text-emerald-400"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M3.5 8.5L6.5 11.5L12.5 4.5"
										stroke="currentColor"
										stroke-width="1.5"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</template>
							<img v-else src="/src/shared/assets/icons/copy-white.svg" alt="" class="h-4 w-4 opacity-70" />
						</span>
					</button>

					<div class="my-2 h-px bg-cardBorder" />

					<button
						type="button"
						class="w-full cursor-pointer rounded-md bg-errorColor/15 px-2 py-2 text-left text-sm text-errorColor transition hover:bg-errorColor/25"
						@click="onDisconnect"
					>
						Disconnect Wallet
					</button>
				</div>
			</Transition>
		</template>
	</div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
	transition:
		opacity 0.15s ease,
		transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
